import type {
  CriarDesenhoRascunhoInput,
  DesenhoMimeType,
  DesenhoRevisaoView,
  DesenhosDoMaterialView,
  PublicarDesenhoInput,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../csrf-fetch';

export interface ArquivoCandidato {
  readonly nome: string;
  readonly tipo: string;
  readonly tamanho: number;
  readonly conteudoBase64: string;
}

export interface ArquivoParaEnvio {
  readonly nome: string;
  readonly mimeType: DesenhoMimeType;
  readonly conteudoBase64: string;
}

export interface LinhaDoTempoRevisao {
  /** Lote autorizado que o servidor resolveu para a OS consultada. */
  readonly loteId: string;
  readonly materialId: string | null;
  readonly efetiva: { readonly revisaoId: string; readonly versao: number } | null;
  readonly versao: number;
  readonly origens: readonly { readonly loteOrigemId: string; readonly tipo: 'SPLIT' | 'MERGE'; readonly revisaoId: string | null }[];
  readonly fatos: readonly { readonly id: string; readonly revisaoId: string | null; readonly revisaoAnteriorId: string | null; readonly origemLoteId: string | null; readonly tipo: string; readonly motivo: string | null; readonly effectiveAt: string }[];
  readonly publicadas: readonly { readonly id: string; readonly codigoRevisao: string }[];
}

export class ErroDesenho extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ErroDesenho';
  }
}

const MIMES: ReadonlySet<string> = new Set(['application/pdf', 'image/png', 'image/jpeg']);
const MAX_BYTES = 15 * 1024 * 1024;

export function erroDeDesenho(status: number): string {
  if (status === 403) return 'Você não tem permissão para esta ação de desenho.';
  if (status === 409) return 'A situação atual mudou. Recarregue as revisões antes de tentar novamente.';
  if (status === 401) return 'Sua sessão expirou. Entre novamente para continuar.';
  return 'Não foi possível concluir a ação sobre o desenho.';
}

export function arquivoParaEnvio(arquivo: ArquivoCandidato): ArquivoParaEnvio | null {
  if (!MIMES.has(arquivo.tipo) || arquivo.tamanho <= 0 || arquivo.tamanho > MAX_BYTES || !arquivo.conteudoBase64) return null;
  return {
    nome: arquivo.nome,
    mimeType: arquivo.tipo as DesenhoMimeType,
    conteudoBase64: arquivo.conteudoBase64,
  };
}

function chaveIdempotencia(): string {
  return globalThis.crypto?.randomUUID?.() ?? `desenho-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function corpoDeErro(resposta: Response): Promise<string> {
  const corpo = (await resposta.json().catch(() => null)) as { message?: unknown } | null;
  if (typeof corpo?.message === 'string' && corpo.message.trim()) return corpo.message;
  return erroDeDesenho(resposta.status);
}

async function exigirResposta<T>(resposta: Response): Promise<T> {
  if (!resposta.ok) throw new ErroDesenho(resposta.status, await corpoDeErro(resposta));
  return resposta.json() as Promise<T>;
}

export async function lerArquivoComoBase64(arquivo: File): Promise<ArquivoParaEnvio | null> {
  const conteudoBase64 = await new Promise<string>((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error('Não foi possível ler o arquivo selecionado.'));
    leitor.onload = () => {
      const valor = typeof leitor.result === 'string' ? leitor.result : '';
      resolve(valor.includes(',') ? (valor.split(',', 2)[1] ?? '') : valor);
    };
    leitor.readAsDataURL(arquivo);
  });
  return arquivoParaEnvio({ nome: arquivo.name, tipo: arquivo.type, tamanho: arquivo.size, conteudoBase64 });
}

export const desenhosApi = {
  listar(materialId: string): Promise<DesenhosDoMaterialView> {
    return apiFetch<DesenhosDoMaterialView>(`/api/v1/materiais/${encodeURIComponent(materialId)}/desenhos`);
  },

  async enviarRascunho(materialId: string, arquivo: ArquivoParaEnvio, codigoRevisao: string, chave = chaveIdempotencia()): Promise<DesenhoRevisaoView> {
    const corpo: CriarDesenhoRascunhoInput = {
      codigoRevisao: codigoRevisao.trim(),
      conteudoBase64: arquivo.conteudoBase64,
      mimeType: arquivo.mimeType,
      chaveIdempotencia: chave,
    };
    return exigirResposta<DesenhoRevisaoView>(await apiMutate(`/api/v1/materiais/${encodeURIComponent(materialId)}/desenhos`, { method: 'POST', body: corpo }));
  },

  async publicar(materialId: string, revisaoId: string, motivo: string, chave = chaveIdempotencia()): Promise<DesenhoRevisaoView> {
    const corpo: PublicarDesenhoInput = { motivo: motivo.trim(), chaveIdempotencia: chave };
    return exigirResposta<DesenhoRevisaoView>(await apiMutate(`/api/v1/materiais/${encodeURIComponent(materialId)}/desenhos/${encodeURIComponent(revisaoId)}/publicar`, { method: 'POST', body: corpo }));
  },

  async arquivo(materialId: string, revisaoId: string): Promise<{ readonly blob: Blob; readonly hashSha256: string | null; readonly isolatedUrl?: string }> {
    const resposta = await fetch(`/api/v1/materiais/${encodeURIComponent(materialId)}/desenhos/${encodeURIComponent(revisaoId)}/arquivo`, { credentials: 'include' });
    if (!resposta.ok) throw new ErroDesenho(resposta.status, await corpoDeErro(resposta));
    // Não recriar o PDF como blob da aplicação quando ele já veio da origem isolada.
    const isolatedUrl = resposta.redirected && new URL(resposta.url).origin !== window.location.origin ? resposta.url : undefined;
    return { blob: await resposta.blob(), hashSha256: resposta.headers.get('X-Checksum-Sha256'), isolatedUrl };
  },

  linhaDoTempo(loteId: string): Promise<LinhaDoTempoRevisao> {
    return apiFetch<LinhaDoTempoRevisao>(`/api/v1/rastreabilidade/lotes/${encodeURIComponent(loteId)}/revisoes`);
  },

  linhaDoTempoDaOs(osId: string): Promise<LinhaDoTempoRevisao> {
    return apiFetch<LinhaDoTempoRevisao>(`/api/v1/rastreabilidade/lotes/os/${encodeURIComponent(osId)}/revisoes`);
  },

  async decidirUniao(loteId: string, revisaoId: string, motivo: string, versaoEsperada: number, chave = chaveIdempotencia()): Promise<{ readonly revisaoId: string; readonly versao: number }> {
    return exigirResposta<{ readonly revisaoId: string; readonly versao: number }>(await apiMutate(`/api/v1/rastreabilidade/lotes/${encodeURIComponent(loteId)}/revisoes/uniao`, {
      method: 'POST',
      body: { revisaoId, motivo: motivo.trim(), versaoEsperada, chaveIdempotencia: chave },
    }));
  },
};
